import { LOGIN_START, LOGIN_SUCCESS, LOGIN_FAILURE, LOGOUT_START, LOGOUT_SUCCESS, LOGOUT_FAILURE } from './type';
import { Dispatch } from 'react';
// COMMENTED OUT: Client-side encryption disabled (see src/utils/encryption.ts for details)
// import { encryptCredentials, isEncryptionSupported } from '../../utils/encryption';

interface Action {
  type: string;
  payload?: any;
  loading?: boolean;
}

export interface LoginResponse {
  success?: boolean;
  loginToken?: string;
  message?: string;
  status?: string;
  passwordChangeRequired?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL;

const clearAuthStorage = (): void => {
  localStorage.removeItem('loginToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('roleId');
  localStorage.removeItem('userName');
  localStorage.removeItem('adRedirectResponse');
  localStorage.removeItem('passwordChangeRequired');
};

export const Logout = () => async (dispatch: Dispatch<Action>): Promise<{ success: boolean; message?: string }> => {
  const token = localStorage.getItem('loginToken');

  try {
    dispatch({ type: LOGOUT_START, loading: true });

    if (token) {
      const response = await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      let res: any = {};
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        res = await response.json();
      }

      if (response.ok || res.status === '200') {
        dispatch({ type: LOGOUT_SUCCESS, loading: false });
      } else {
        dispatch({
          type: LOGOUT_FAILURE,
          payload: res.message || res.exceptionMsg || 'Logout failed',
          loading: false,
        });
      }
    } else {
      dispatch({ type: LOGOUT_SUCCESS, loading: false });
    }

    clearAuthStorage();
    return { success: true };
  } catch (error: any) {
    dispatch({ type: LOGOUT_FAILURE, payload: error.message, loading: false });
    clearAuthStorage();
    return { success: true, message: error.message };
  }
};

export const Login = (
  credentials: { username: string; password: string },
) => async (dispatch: Dispatch<Action>): Promise<LoginResponse> => {
  try {
    dispatch({ type: LOGIN_START, loading: true });

    // Standard payload - relies on HTTPS/TLS for credential protection
    const payload = {
      username: credentials.username,
      password: credentials.password,
    };

    /* COMMENTED OUT: Client-side encryption logic (disabled for now)
    // Prepare payload with client-side encryption
    let payload: any = { username: credentials.username };

    // Check if encryption is supported and encrypt password
    if (isEncryptionSupported()) {
      try {
        const encryptedPayload = await encryptCredentials(credentials.password);
        payload.encryptedPassword = encryptedPayload.encryptedData;
        if (encryptedPayload.keyId) {
          payload.keyId = encryptedPayload.keyId;
        }
        payload.encrypted = true;
      } catch (encryptionError) {
        console.warn('Encryption failed, falling back to HTTPS-only:', encryptionError);
        // Fallback to plaintext over HTTPS if encryption fails
        payload.password = credentials.password;
        payload.encrypted = false;
      }
    } else {
      // Browser doesn't support Web Crypto API
      console.warn('Web Crypto API not supported, using HTTPS-only');
      payload.password = credentials.password;
      payload.encrypted = false;
    }
    */

    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const res = await response.json();
    if (res.loginToken) {
      localStorage.setItem('loginToken', res.loginToken);
      if (res.refreshToken) localStorage.setItem('refreshToken', res.refreshToken);
      if (res.roleId) localStorage.setItem('roleId', res.roleId);
      dispatch({ type: LOGIN_SUCCESS, payload: res, loading: false });
      return { success: true, ...res };
    } else if (res.status === '1010') {
      dispatch({ type: LOGIN_FAILURE, payload: res.message, loading: false });
      return { success: false, status: '1010', message: res.message };
    } else {
      dispatch({ type: LOGIN_FAILURE, payload: res.message, loading: false });
      return { success: false, ...res };
    }
  } catch (error: any) {
    dispatch({ type: LOGIN_FAILURE, payload: error.message, loading: false });
    localStorage.removeItem('loginToken');
    return { success: false, message: error.message };
  }
};

export const adLogin = (
  email: string,
  idToken: string,
) => async (dispatch: Dispatch<Action>): Promise<any> => {
  try {
    dispatch({ type: LOGIN_START, loading: true });

    const response = await fetch(`${API_URL}/auth/ad-login/${email}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'idToken': idToken },
    });
    const res = await response.json();
    if (res.loginToken) {
      localStorage.setItem('loginToken', res.loginToken);
      if (res.refreshToken) localStorage.setItem('refreshToken', res.refreshToken);
      if (res.roleId) localStorage.setItem('roleId', res.roleId);
      dispatch({ type: LOGIN_SUCCESS, payload: res, loading: false });
      return { success: true, ...res };
    } else {
      dispatch({ type: LOGIN_FAILURE, payload: res.message, loading: false });
      return { success: false, ...res };
    }
  } catch (error: any) {
    dispatch({ type: LOGIN_FAILURE, payload: error.message, loading: false });
    return { success: false, message: error.message };
  }
};

export const ldapLogin = (
  credentials: { username: string; password: string },
) => async (dispatch: Dispatch<Action>): Promise<LoginResponse> => {
  try {
    dispatch({ type: LOGIN_START, loading: true });

    // Standard payload - relies on HTTPS/TLS for credential protection
    const payload = {
      username: credentials.username,
      password: credentials.password,
    };

    /* COMMENTED OUT: Client-side encryption logic (disabled for now)
    // Prepare payload with client-side encryption
    let payload: any = { username: credentials.username };

    // Check if encryption is supported and encrypt password
    if (isEncryptionSupported()) {
      try {
        const encryptedPayload = await encryptCredentials(credentials.password);
        payload.encryptedPassword = encryptedPayload.encryptedData;
        if (encryptedPayload.keyId) {
          payload.keyId = encryptedPayload.keyId;
        }
        payload.encrypted = true;
      } catch (encryptionError) {
        console.warn('Encryption failed, falling back to HTTPS-only:', encryptionError);
        // Fallback to plaintext over HTTPS if encryption fails
        payload.password = credentials.password;
        payload.encrypted = false;
      }
    } else {
      // Browser doesn't support Web Crypto API
      console.warn('Web Crypto API not supported, using HTTPS-only');
      payload.password = credentials.password;
      payload.encrypted = false;
    }
    */

    const response = await fetch(`${API_URL}/auth/ldap-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const res = await response.json();
    if (res.loginToken) {
      localStorage.setItem('loginToken', res.loginToken);
      if (res.refreshToken) localStorage.setItem('refreshToken', res.refreshToken);
      if (res.roleId) localStorage.setItem('roleId', res.roleId);
      dispatch({ type: LOGIN_SUCCESS, payload: res, loading: false });
      return { success: true, ...res };
    } else {
      dispatch({ type: LOGIN_FAILURE, payload: res.message, loading: false });
      return { success: false, ...res };
    }
  } catch (error: any) {
    dispatch({ type: LOGIN_FAILURE, payload: error.message, loading: false });
    return { success: false, message: error.message };
  }
};
